/**
 * Pure model helpers for language variants (lot D step 5) and place picks (lot E step 11).
 * No React, no API calls, no side effects.
 *
 * A row's language is DEFAULTED (D « Families with several places »):
 * `(row.language ?? brand working language).toLowerCase()`, so an unlabelled
 * root holds the working language (`brand.strategy?.workingLanguage ?? 'fr'`).
 *
 * @see docs/plans/prompt-studio-build/D-language-variants.md §5
 */
import { placeKey } from '@traaker/shared';
import type { BrandQuery, BrandQueryAxis, CreateZoneVariantsResponse, Location } from '@traaker/shared';
import type { Family, LimitSlot, PromptAllowanceFigures } from './promptStudioModel';
import { MONDIAL, needRowsReason, structuralRowError } from './promptStudioModel';

/** The defaulted language of a row. */
export function rowLanguage(row: BrandQuery, workingLanguage: string): string {
  return (row.language ?? workingLanguage).toLowerCase();
}

const activeRows = (f: Family) => f.rows.filter((row) => row.status === 'active');

// ---------------------------------------------------------------------------
// familyLanguages
// ---------------------------------------------------------------------------

/** Distinct defaulted languages of the family's ACTIVE rows. */
export function familyLanguages(f: Family, workingLanguage: string): string[] {
  return [...new Set(activeRows(f).map((row) => rowLanguage(row, workingLanguage)))];
}

// ---------------------------------------------------------------------------
// familyPlaces
// ---------------------------------------------------------------------------

/**
 * Distinct place keys (C1 placeKey) of the ACTIVE rows in a family.
 * '∅' = Mondial (row has neither zoneMeta nor zone).
 * 'legacy:<zone>' = unresolved legacy zone — never Mondial.
 */
export function familyPlaces(f: Family, locations: Location[]): string[] {
  return [...new Set(activeRows(f).map((row) => placeKey(row, locations)))];
}

/** Number of distinct places each active language is measured at (picker « {k} sur {n} lieux »). */
export function languagePlaceCounts(f: Family, locations: Location[], workingLanguage: string): Map<string, number> {
  const places = new Map<string, Set<string>>();
  for (const row of activeRows(f)) {
    const lang = rowLanguage(row, workingLanguage);
    const set = places.get(lang) ?? new Set<string>();
    set.add(placeKey(row, locations));
    places.set(lang, set);
  }
  return new Map([...places].map(([lang, set]) => [lang, set.size]));
}

// ---------------------------------------------------------------------------
// familyGrid
// ---------------------------------------------------------------------------

/**
 * The language × place grid AFTER the proposed change.
 *
 * @param addLangs     languages being added (queued, not yet written)
 * @param addPlaces    places being added (lot E: one row per family language at each)
 * @param removePlaces places being unticked (lot E: their rows deleted in every language)
 */
export function familyGrid(
  f: Family,
  locations: Location[],
  workingLanguage: string,
  addLangs: string[],
  addPlaces: string[],
  removePlaces: string[] = [],
): { langs: number; places: number; holes: number } {
  const rows = activeRows(f).filter((row) => !removePlaces.includes(placeKey(row, locations)));
  const famLangs = familyLanguages(f, workingLanguage);
  const langs = new Set([
    ...rows.map((row) => rowLanguage(row, workingLanguage)),
    ...(addPlaces.length > 0 ? famLangs : []),
    ...addLangs,
  ]);
  const places = new Set([...rows.map((row) => placeKey(row, locations)), ...addPlaces]);
  const cells = new Set(rows.map((row) => `${rowLanguage(row, workingLanguage)}|${placeKey(row, locations)}`));
  for (const lang of addLangs) for (const place of places) cells.add(`${lang}|${place}`);
  for (const place of addPlaces) for (const lang of famLangs) cells.add(`${lang}|${place}`);
  const nPlaces = Math.max(1, places.size);
  return { langs: langs.size, places: nPlaces, holes: Math.max(0, langs.size * nPlaces - cells.size) };
}

// ---------------------------------------------------------------------------
// atCap
// ---------------------------------------------------------------------------

/**
 * Display-only: true when the allowance is loaded (not null) and exhausted.
 * null means -1 / agency / not loaded → never at cap.
 */
export function atCap(left: number | null): boolean {
  return left !== null && left <= 0;
}

// ---------------------------------------------------------------------------
// rowsOfLanguage
// ---------------------------------------------------------------------------

/** Active rows of a defaulted language, every place (LanguageRemoveDialog, step 11). */
export function rowsOfLanguage(f: Family, lang: string, workingLanguage: string): BrandQuery[] {
  return activeRows(f).filter((row) => rowLanguage(row, workingLanguage) === lang);
}

// ---------------------------------------------------------------------------
// translatePrecheck (step 8)
// ---------------------------------------------------------------------------

/**
 * [B] « Traduire et enregistrer » greyed BEFORE translating (UI-SPEC §2.6):
 * rows added = queued languages × family places (a queued language has no
 * active row anywhere). null = enabled; `figures` null (agency) or no
 * `fitsByAxis` (allowance -1) never greys.
 */
export function translatePrecheck(
  queued: number,
  places: number,
  figures: PromptAllowanceFigures | null,
  axis: BrandQueryAxis,
): LimitSlot | null {
  return needRowsReason(queued * places, figures, axis);
}

// ---------------------------------------------------------------------------
// Place pick (lot E step 11, UI-SPEC §2.10 « Opened prompt, Lieu, lot E (multi) »)
// ---------------------------------------------------------------------------

/** The opened family's places (familyPlaces) and the root's place (C1 placeKey). */
export interface PlaceCtx {
  current: string[];
  rootKey: string;
}

/**
 * Rows not clickable in the place picker. The root's place: on a multi-place
 * family, or while the pick keeps ≥ 2 other places (unticking the root's place
 * is only ever the one-place replace). Mondial, when it is not a family place,
 * locks with it: the zone-variants route takes zones only, so Mondial is only
 * reachable as a replace.
 */
export function lockedPlaces(ctx: PlaceCtx, ticked: string[]): string[] {
  const rootLocked = ctx.current.length > 1 || ticked.filter((k) => k !== ctx.rootKey).length > 1;
  if (!rootLocked) return [];
  return ctx.current.includes(MONDIAL) || ctx.rootKey === MONDIAL ? [ctx.rootKey] : [ctx.rootKey, MONDIAL];
}

/**
 * A click in the place picker. The last place never unticks; on a one-place
 * family whose root place is unticked the pick is the replace, so it stays
 * single-select; ticking Mondial (not a family place) is a replace too.
 */
export function togglePlace(ctx: PlaceCtx, ticked: string[], key: string): string[] {
  if (lockedPlaces(ctx, ticked).includes(key)) return ticked;
  if (ticked.includes(key)) return ticked.length === 1 ? ticked : ticked.filter((k) => k !== key);
  if (key === MONDIAL && !ctx.current.includes(MONDIAL)) return [MONDIAL];
  if (ctx.current.length === 1 && !ticked.includes(ctx.rootKey) && key !== ctx.rootKey) return [key];
  return [...ticked, key];
}

export type PlacePlan =
  | { kind: 'none' }
  | { kind: 'replace'; key: string }
  | { kind: 'variants'; add: string[]; remove: string[] };

/**
 * What « Enregistrer » sends for a pick (`ticked` null = nothing picked):
 * a set equal to the current one sends nothing (R3); a one-place family left
 * with one other place = ONE C11 PATCH (replace); otherwise one zone-variants
 * call for the added places and a delete per row of each unticked place.
 */
export function placePlan(current: string[], ticked: string[] | null): PlacePlan {
  if (!ticked) return { kind: 'none' };
  const add = ticked.filter((k) => !current.includes(k));
  const remove = current.filter((k) => !ticked.includes(k));
  if (add.length === 0 && remove.length === 0) return { kind: 'none' };
  if (current.length === 1 && ticked.length === 1) return { kind: 'replace', key: ticked[0] };
  return { kind: 'variants', add, remove };
}

/** Rows a zone-variants call adds: one per family language at each added place. */
export function placeRowsAdded(f: Family, workingLanguage: string, added: number): number {
  return familyLanguages(f, workingLanguage).length * added;
}

/** Active rows at the unticked places, every language (one soft delete each). */
export function rowsAtPlaces(f: Family, locations: Location[], keys: string[]): BrandQuery[] {
  return activeRows(f).filter((row) => keys.includes(placeKey(row, locations)));
}

/**
 * The lines under the action row for a zone-variants save's per-row errors
 * (the created rows stay): the structural limits (U14, no number), else the
 * generic save failure; one line per distinct reason.
 */
export function zoneRowErrors(res: Pick<CreateZoneVariantsResponse, 'errors'>, axis: BrandQueryAxis): LimitSlot[] {
  const out = new Map<string, LimitSlot>();
  for (const e of res.errors ?? []) {
    const slot = structuralRowError(e.error, axis, 'save') ?? { key: 'promptStudio.common.saveFailed', values: {} };
    if (!out.has(slot.key)) out.set(slot.key, slot);
  }
  return [...out.values()];
}

// ---------------------------------------------------------------------------
// languageName
// ---------------------------------------------------------------------------

/** A language code's name in the UI language (« allemand » / German). */
export function languageName(code: string, uiLang: string): string {
  try {
    return new Intl.DisplayNames([uiLang || 'fr'], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}
